import React, { useState, useEffect } from 'react';
import { getRecentFeaturesForUser, getUserFeatureGroups } from '../services/featureService';
import { formatRelativeDate } from '../utils/dateUtils';
import { logger } from '../utils/logger';
import './RecentFeatures.css';

// Helper function to format updated_at date (same as Home.js)
const formatUpdatedAt = (dateString) => {
  if (!dateString) return '';
  return formatRelativeDate(dateString);
};

const RecentFeatures = ({ onClose, onNavigateToFeature }) => {
  const [features, setFeatures] = useState([]);
  const [featureGroups, setFeatureGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRecentFeatures();
  }, []);

  const loadRecentFeatures = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load feature groups and recent features in parallel
      const [groupsResult, featuresResult] = await Promise.allSettled([
        getUserFeatureGroups(),
        getRecentFeaturesForUser(10)
      ]);

      if (groupsResult.status === 'fulfilled') {
        setFeatureGroups(groupsResult.value);
      }

      if (featuresResult.status === 'fulfilled') {
        setFeatures(featuresResult.value);
      } else {
        setError('Failed to load recent features');
        logger.error('Error loading recent features:', featuresResult.reason);
      }
    } catch (err) {
      setError('Failed to load recent features');
      logger.error('Error loading recent features:', err);
    } finally {
      setLoading(false);
    }
  };

  const getMapNameForFeature = (featureGroupId) => {
    const group = featureGroups.find(g => g.id === featureGroupId);
    return group ? group.display_name : 'Unknown Map';
  };

  const handleFeatureClick = (featureId) => {
    if (onNavigateToFeature) {
      onNavigateToFeature(featureId);
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="recent-features-modal-overlay" onClick={onClose}>
      <div className="recent-features-modal" onClick={(e) => e.stopPropagation()}>
        <div className="recent-features-header">
          <h3>Recent Features</h3>
          <button className="recent-features-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="recent-features-content">
          {loading && (
            <div className="recent-features-loading">
              <i className="fas fa-spinner fa-spin"></i> Loading...
            </div>
          )}
          
          {!loading && error && (
            <div className="recent-features-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}
          
          {!loading && !error && features.length === 0 && (
            <div className="recent-features-empty">
              No recently updated features found.
            </div>
          )}
          
          {!loading && !error && features.length > 0 && (
            <ul className="recent-features-list">
              {features.map((feature) => (
                <li key={feature.id} className="recent-feature-item">
                  <button
                    type="button"
                    className="recent-feature-button"
                    onClick={() => handleFeatureClick(feature.id)}
                  >
                    <div className="recent-feature-name">{feature.name}</div>
                    <div className="recent-feature-meta">
                      <span className="recent-feature-map">
                        {getMapNameForFeature(feature.feature_group_id)}
                      </span>
                      <span className="recent-feature-updated">
                        {formatUpdatedAt(feature.updated_at)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecentFeatures;

